import { useQueryClient } from '@tanstack/react-query';
import { PostStep } from '../post/PostStep';
import { useNavigate } from '@tanstack/react-router';
import { ListChecks, Rocket } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  JOB_BUILDER_SECTION_LABELS,
  isJobBuilderSection,
  openBlockers,
  type JobBuilderSection,
  type JobDetail,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { ModuleGate } from '../../../../components/ModuleGate';
import { Popover, usePopover } from '../../../../components/Popover';
import { errorMessage } from '../../../../lib/api-client';
import { useModule } from '../../../../lib/modules';
import { registerCommands } from '../../../../shell/command-registry';
import { useToasts } from '../../../../stores/toasts';
import { icpKey } from '../../../icp/api/icp-api';
import { jobKey, useOpenJob } from '../../api/jobs-api';
import { createSaveLane, SaveLaneContext } from '../../hooks/save-lane';
import { BasicsStep } from '../BasicsStep';
import { FormStep } from '../FormStep';
import { OfferStep } from '../OfferStep';
import { SaveIndicator } from '../SaveIndicator';
import { SetupShortcuts } from '../SetupShortcuts';
import { AtsSummary } from './AtsSummary';
import { BriefEditor } from './BriefEditor';
import { FormLinks } from './FormLinks';
import { SaveReportContext, createSaveReports, useOverallSaveState, type SaveReports } from './builder-saves';
import { CriteriaSection, MatrixSection, StagesSection, reportAs } from './BuilderSections';
import { ProfileLinesContext, createProfileLinesStore } from './profile-lines-store';
import { ProfileSection } from './ProfileSection';
import { ProfileSummary } from './ProfileSummary';
import { SectionNav } from './SectionNav';
import styles from './builder.module.css';

/**
 * JOB-13 (R3; PRD §4.1, QoL §7) — a job set up on one page. Sections down the left, each ticked
 * when complete; the section in the middle; on a wide screen, what the ranking will look for on the
 * right (the ideal candidate's lines, or the screening criteria's when that module is on). Everything saves as you type, through one lane so no two saves race. Save state and
 * "Open job" sit in a bar that never scrolls away (QoL §7 rule 7). It is stage ① Job on JOB-12.
 */
export function JobBuilder({ detail, section }: { readonly detail: JobDetail; readonly section: JobBuilderSection | null }): ReactNode {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const show = useToasts((toasts) => toasts.show);
  const openJob = useOpenJob();
  const isCareerSiteOn = useModule('career_site');
  const [lane] = useState(() => createSaveLane(detail.version));
  const [reports] = useState(createSaveReports);
  const [profileLines] = useState(createProfileLinesStore);
  const unsavedWarning = usePopover();
  // Bumped when the setup is replaced (a template, a copy, someone else's save): sections reload.
  const [epoch, setEpoch] = useState(0);

  useEffect(() => lane.follow(detail.version), [lane, detail.version]);

  // 06-qol §1: while the builder is open, each of its sections is one palette entry away.
  const sectionKeys = detail.sections.filter((state) => state.isShown).map((state) => state.section).join(',');
  const title = detail.publicTitle ?? detail.title;
  useEffect(() => {
    const keys = sectionKeys.split(',').filter(isJobBuilderSection);
    return registerCommands(
      keys.map((key) => ({
        id: `job.setup.${detail.id}.${key}`,
        label: `Set up ${JOB_BUILDER_SECTION_LABELS[key].toLowerCase()}`,
        section: 'action' as const,
        context: title,
        permission: 'job_config' as const,
        icon: ListChecks,
        keywords: ['setup', 'builder', 'job'],
        run: () => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section: key } }),
      })),
    );
  }, [detail.id, title, sectionKeys, navigate]);

  const shown = detail.sections.filter((state) => state.isShown);
  const current = section !== null && shown.some((state) => state.section === section) ? section : (shown.find((state) => !state.isComplete)?.section ?? 'basics');
  const isReadOnly = !detail.actions.includes('edit');
  const canReplace = detail.actions.includes('apply_template') || detail.actions.includes('copy_setup');
  const blockers = openBlockers(detail.sections);
  const isProfileShown = shown.some((state) => state.section === 'profile');

  async function onConflict(message: string): Promise<void> {
    show({ tone: 'warning', message: 'Someone else changed this job', detail: message });
    await queryClient.refetchQueries({ queryKey: jobKey(detail.id) });
    replaced();
  }

  /** The setup was replaced (a template, a copy, someone else's save): sections reload, and so does the ideal candidate. */
  function replaced(): void {
    void queryClient.invalidateQueries({ queryKey: icpKey(detail.id) });
    setEpoch((value) => value + 1);
  }

  /** F5.12: saves anything waiting, then opens — or says what's still missing. */
  async function open(): Promise<void> {
    await reports.flushAll();
    await lane.idle();
    // The ideal candidate is saved by its own button (saving can cost money), so "Open job" asks before going ahead without it.
    if (reports.hasUnsaved()) {
      unsavedWarning.open();
      return;
    }
    const fresh = queryClient.getQueryData<JobDetail>(jobKey(detail.id)) ?? detail;
    const missing = openBlockers(fresh.sections);
    const first = missing[0];
    if (first) {
      show({ tone: 'warning', message: `${JOB_BUILDER_SECTION_LABELS[first.section]} isn't finished`, detail: first.missing.join(' · ') });
      void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section: first.section } });
      return;
    }
    try {
      const opened = await lane.run((version) => openJob.mutateAsync({ id: detail.id, version }));
      show({
        tone: 'success',
        message: `${opened.publicTitle ?? opened.title} is open`,
        detail: isCareerSiteOn ? 'Posting it to the career site comes next.' : 'Add candidates to it next.',
      });
    } catch (cause) {
      show({ tone: 'danger', message: "The job didn't open", detail: errorMessage(cause) });
    }
  }

  /** "Save and open": the unsaved ideal candidate is saved first, then the job opens as it would have. */
  async function saveAndOpen(): Promise<void> {
    unsavedWarning.close();
    if (await reports.saveUnsaved()) await open();
  }

  const props = { job: detail, isReadOnly, onConflict: (message: string) => void onConflict(message) };
  // The ideal candidate is drawn separately below: it stays mounted while another section shows, so what was typed is not lost.
  const content: Record<Exclude<JobBuilderSection, 'profile'>, () => ReactNode> = {
    basics: () => <BasicsStep {...props} footer={reportAs('basics')} />,
    brief: () => <BriefEditor {...props} footer={reportAs('brief')} />,
    criteria: () => (
      <ModuleGate module="screening_criteria">
        <CriteriaSection job={detail} isReadOnly={isReadOnly} />
      </ModuleGate>
    ),
    matrix: () => <MatrixSection job={detail} isReadOnly={isReadOnly} />,
    stages: () => <StagesSection {...props} />,
    // Owner 2026-11-01: Form links sit under Application form, optional, and no part of opening the job.
    form: () => (
      <div className={styles.section}>
        <FormStep {...props} footer={reportAs('form')} />
        <FormLinks job={detail} />
      </div>
    ),
    offer: () => <OfferStep {...props} footer={reportAs('offer')} />,
    post: () => (
      <ModuleGate module="job_image">
        <PostStep job={detail} isReadOnly={isReadOnly} />
      </ModuleGate>
    ),
  };

  return (
    <SaveLaneContext.Provider value={lane}>
      <SaveReportContext.Provider value={reports}>
        <ProfileLinesContext.Provider value={profileLines}>
          <div className={styles.builder}>
            {canReplace && !isReadOnly && <SetupShortcuts job={detail} onReplaced={replaced} />}
            <div className={styles.layout}>
              <SectionNav
                sections={detail.sections}
                current={current}
                hrefOf={(key) => `/jobs/${detail.id}/setup/${key}`}
                onSelect={(key) => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section: key } })}
              />
              <div className={styles.middle}>
                {isProfileShown && (
                  <div key={`profile:${epoch}`} hidden={current !== 'profile'} className={styles.swap}>
                    <ProfileSection {...props} />
                  </div>
                )}
                {current !== 'profile' && (
                  <div key={`${current}:${epoch}`} className={styles.swap}>
                    {content[current]()}
                  </div>
                )}
              </div>
              {current === 'profile' && <ProfileSummary />}
              {current === 'criteria' && (
                <ModuleGate module="screening_criteria">
                  <AtsSummary criteria={detail.criteria} />
                </ModuleGate>
              )}
            </div>
            <footer className={styles.footer}>
              <div className={styles.footerStatus}>
                <OverallSaveIndicator reports={reports} />
                {detail.status === 'draft' && blockers.length > 0 && (
                  <span className={styles.footerMissing}>
                    {blockers.length === 1 ? '1 section to finish' : `${blockers.length} sections to finish`}
                  </span>
                )}
              </div>
              {detail.actions.includes('open') && (
                <>
                  <Button ref={unsavedWarning.triggerRef} variant="solid" icon={Rocket} isPending={openJob.isPending} onClick={() => void open()}>
                    Open job
                  </Button>
                  <Popover anchor={unsavedWarning.anchor} onClose={unsavedWarning.close} align="right" width={340} label="Unsaved ideal candidate">
                    <div className={styles.unsavedWarning}>
                      <strong>You have unsaved lines in the ideal candidate.</strong>
                      <span>
                        {blockers.some((state) => state.section === 'profile')
                          ? 'Opening now would use the last saved ratings, which have no line rated 5.'
                          : 'Opening now would use the last saved lines.'}
                      </span>
                      <div className={styles.unsavedActions}>
                        <Button size="sm" variant="solid" onClick={() => void saveAndOpen()}>
                          Save and open
                        </Button>
                        <Button size="sm" onClick={unsavedWarning.close}>
                          Go back
                        </Button>
                      </div>
                    </div>
                  </Popover>
                </>
              )}
            </footer>
          </div>
        </ProfileLinesContext.Provider>
      </SaveReportContext.Provider>
    </SaveLaneContext.Provider>
  );
}

function OverallSaveIndicator({ reports }: { readonly reports: SaveReports }): ReactNode {
  return <SaveIndicator state={useOverallSaveState(reports)} />;
}
